import React from 'react';
import DateTimePickerInput from '../../../components/DateTimePickerInput';
import {
  CAB_REPEAT_OPTIONS,
  CAB_WEEKDAY_OPTIONS,
  getRepeatLabel,
} from '../../../constants/cabScheduleOptions';
import type { CabRepeatValue, ScheduleEntryFormData } from '../../../types/cabSchedule';
import {
  computeDefaultRepeatsUntil,
  type ScheduleEntryFormErrors,
} from '../../../utils/cabScheduleValidation';

interface ScheduleEntryFormProps {
  formData: ScheduleEntryFormData;
  errors: ScheduleEntryFormErrors;
  onChange: (updates: Partial<ScheduleEntryFormData>) => void;
  onSubmit: () => void;
  onCancel: () => void;
  isSaving?: boolean;
}

/** Shared label + control row so Name / When / From / Repeats stay aligned. */
function FormRow({
  label,
  htmlFor,
  required,
  children,
  labelAs = 'label',
}: {
  label: string;
  htmlFor?: string;
  required?: boolean;
  children: React.ReactNode;
  labelAs?: 'label' | 'span' | 'heading';
}) {
  const labelClassName = 'text-sm font-medium text-gray-700';
  const headingClassName = 'text-sm font-semibold text-gray-800';

  return (
    <div className="grid grid-cols-12 gap-3 items-start">
      <div className="col-span-12 sm:col-span-3 pt-2 text-right sm:pr-2">
        {labelAs === 'heading' ? (
          <h2 className={headingClassName}>{label}</h2>
        ) : labelAs === 'span' ? (
          <span className={labelClassName}>
            {required && <span className="text-red-500 mr-0.5" aria-hidden>*</span>}
            {label}
          </span>
        ) : (
          <label htmlFor={htmlFor} className={labelClassName}>
            {required && <span className="text-red-500 mr-0.5" aria-hidden>*</span>}
            {label}
          </label>
        )}
      </div>
      {children != null && <div className="col-span-12 sm:col-span-9">{children}</div>}
    </div>
  );
}

const ScheduleEntryForm: React.FC<ScheduleEntryFormProps> = ({
  formData,
  errors,
  onChange,
  onSubmit,
  onCancel,
  isSaving = false,
}) => {
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit();
  };

  const applyAutoRepeatsUntil = (
    updates: Partial<ScheduleEntryFormData>,
    fromDate: string,
    repeats: CabRepeatValue
  ) => {
    const autoUntil = computeDefaultRepeatsUntil(fromDate, repeats);
    if (autoUntil) {
      updates.repeatsUntil = autoUntil;
    }
    onChange(updates);
  };

  const handleFromDateChange = (value: string) => {
    applyAutoRepeatsUntil({ fromDate: value }, value, formData.repeats);
  };

  const handleRepeatsChange = (value: CabRepeatValue) => {
    const updates: Partial<ScheduleEntryFormData> = { repeats: value };
    if (value === 'specific') {
      updates.repeatInterval = formData.repeatInterval || 1;
      if (!formData.repeatWeekdays.length) {
        const dateOnly = formData.fromDate.includes('T')
          ? formData.fromDate.split('T')[0]
          : formData.fromDate;
        const [y, m, d] = dateOnly.split('-').map(Number);
        if (![y, m, d].some((n) => Number.isNaN(n))) {
          updates.repeatWeekdays = [new Date(y, m - 1, d).getDay()];
        }
      }
    }
    applyAutoRepeatsUntil(updates, formData.fromDate, value);
  };

  const toggleWeekday = (day: number) => {
    const selected = new Set(formData.repeatWeekdays);
    if (selected.has(day)) selected.delete(day);
    else selected.add(day);
    onChange({ repeatWeekdays: [...selected] });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6 max-w-4xl" noValidate>
      <div className="border-b border-gray-200 pb-6">
        <FormRow label="Name" htmlFor="schedule-name" required>
          <input
            id="schedule-name"
            type="text"
            value={formData.name}
            maxLength={255}
            onChange={(e) => onChange({ name: e.target.value })}
            className={`w-full px-3 py-1.5 border rounded text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500 ${
              errors.name ? 'border-red-500' : 'border-gray-300'
            }`}
          />
          {errors.name && <p className="mt-1 text-xs text-red-600">{errors.name}</p>}
        </FormRow>
      </div>

      <div className="border-b border-gray-200 pb-6 space-y-4">
        <FormRow label="When" labelAs="heading">{null}</FormRow>

        <FormRow label="From" labelAs="span" required>
          <div className="flex flex-wrap items-center gap-2">
            <div className="w-40">
              <DateTimePickerInput
                id="from-date"
                value={formData.fromDate}
                onChange={handleFromDateChange}
                showTimeSelect={false}
                error={Boolean(errors.fromDate || errors.dateTimeRange)}
              />
            </div>
            {!formData.allDay && (
              <input
                type="time"
                value={formData.fromTime}
                onChange={(e) => onChange({ fromTime: e.target.value })}
                className={`px-2 py-1.5 border rounded text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500 ${
                  errors.fromTime || errors.dateTimeRange ? 'border-red-500' : 'border-gray-300'
                }`}
                aria-label="From time"
              />
            )}
            <span className="text-sm text-gray-500 px-1">To</span>
            {!formData.allDay && (
              <input
                type="time"
                value={formData.toTime}
                onChange={(e) => onChange({ toTime: e.target.value })}
                className={`px-2 py-1.5 border rounded text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500 ${
                  errors.toTime || errors.dateTimeRange ? 'border-red-500' : 'border-gray-300'
                }`}
                aria-label="To time"
              />
            )}
            <div className="w-40">
              <DateTimePickerInput
                id="to-date"
                value={formData.toDate}
                onChange={(v) => onChange({ toDate: v })}
                showTimeSelect={false}
                error={Boolean(errors.toDate || errors.dateTimeRange)}
              />
            </div>
            <label className="inline-flex items-center gap-2 text-sm text-gray-700 ml-2">
              <input
                type="checkbox"
                checked={formData.allDay}
                onChange={(e) => onChange({ allDay: e.target.checked })}
                className="h-4 w-4 text-indigo-600 border-gray-300 rounded"
              />
              All day
            </label>
          </div>
          {(errors.fromDate ||
            errors.fromTime ||
            errors.toDate ||
            errors.toTime ||
            errors.dateTimeRange) && (
            <p className="mt-2 text-xs text-red-600">
              {errors.dateTimeRange ||
                errors.fromDate ||
                errors.fromTime ||
                errors.toDate ||
                errors.toTime}
            </p>
          )}
          <p className="mt-3 text-xs text-blue-700 bg-blue-50 border border-blue-100 rounded px-3 py-2 max-w-2xl">
            Please set the schedule greater than one minute to obtain optimal performance.
          </p>
        </FormRow>
      </div>

      <div className="space-y-4">
        <FormRow label="Repeats" htmlFor="schedule-repeats" required>
          <select
            id="schedule-repeats"
            value={formData.repeats}
            onChange={(e) => handleRepeatsChange(e.target.value as CabRepeatValue)}
            className={`w-full max-w-md px-3 py-1.5 border rounded text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500 ${
              errors.repeats ? 'border-red-500' : 'border-gray-300'
            }`}
          >
            {CAB_REPEAT_OPTIONS.map((opt) => (
              <option key={opt.value || 'none'} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          {errors.repeats && <p className="mt-1 text-xs text-red-600">{errors.repeats}</p>}
          <p className="mt-2 text-xs text-blue-700 bg-blue-50 border border-blue-100 rounded px-3 py-2 inline-block">
            {getRepeatLabel(formData.repeats)}
          </p>
        </FormRow>

        {formData.repeats === 'specific' && (
          <>
            <FormRow label="Repeat every" htmlFor="repeat-every" required>
              <div>
                <input
                  id="repeat-every"
                  type="number"
                  min={1}
                  step={1}
                  value={formData.repeatInterval}
                  onChange={(e) => {
                    const raw = e.target.value;
                    onChange({
                      repeatInterval: raw === '' ? '' : Math.max(1, Number(raw) || 1),
                    });
                  }}
                  className={`w-20 px-3 py-1.5 border rounded text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500 ${
                    errors.repeatInterval ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                <p className="mt-1 text-xs text-gray-500">specific</p>
                {errors.repeatInterval && (
                  <p className="mt-1 text-xs text-red-600">{errors.repeatInterval}</p>
                )}
              </div>
            </FormRow>

            <FormRow label="Repeat on" required>
              <div>
                <div className="flex flex-wrap items-center gap-4">
                  {CAB_WEEKDAY_OPTIONS.map((day) => (
                    <label
                      key={day.value}
                      className="inline-flex items-center gap-1.5 text-sm text-gray-700"
                    >
                      <input
                        type="checkbox"
                        checked={formData.repeatWeekdays.includes(day.value)}
                        onChange={() => toggleWeekday(day.value)}
                        className="h-4 w-4 text-indigo-600 border-gray-300 rounded"
                      />
                      {day.label}
                    </label>
                  ))}
                </div>
                {errors.repeatWeekdays && (
                  <p className="mt-1 text-xs text-red-600">{errors.repeatWeekdays}</p>
                )}
              </div>
            </FormRow>
          </>
        )}

        {formData.repeats !== '' && (
          <FormRow
            label={formData.repeats === 'specific' ? 'Repeat until' : 'Repeats Until'}
            htmlFor="repeats-until"
            required
          >
            <div className="w-40">
              <DateTimePickerInput
                id="repeats-until"
                value={formData.repeatsUntil}
                onChange={(v) => onChange({ repeatsUntil: v })}
                showTimeSelect={false}
                error={Boolean(errors.repeatsUntil)}
              />
            </div>
            {errors.repeatsUntil && (
              <p className="mt-1 text-xs text-red-600">{errors.repeatsUntil}</p>
            )}
          </FormRow>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-3 pt-4 border-t border-gray-200">
        <button
          type="submit"
          disabled={isSaving}
          className="px-4 py-2 text-sm text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:opacity-50"
        >
          {isSaving ? 'Submitting...' : 'Submit'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          disabled={isSaving}
          className="px-4 py-2 text-sm border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
        >
          Cancel
        </button>
      </div>
    </form>
  );
};

export default ScheduleEntryForm;
